import {
    Button,
    Divider,
    FormControl,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18next, { t } from 'i18next';
import { useFormikEditUser } from '../../../../hooks/useFormikAddUser';
import { SubmitButton } from '../../../../component/buttons';
import { useFetchUser } from '../../../../hooks/useUser';
import { TextFieldName } from '../forms/textFieldName';
import { BirthdatePicker } from '../forms/birthdatePicker';
import { AvatarPicker } from '../forms/avatarPicker';
import { SexSelector } from '../forms/sexSelector';
import { GenderSelector } from '../forms/genderSelector';
import { TextFieldPhone } from '../forms/textFieldPhone';
import { TextFieldAddress } from '../forms/textFieldAddress';
import { TextFieldZip } from '../forms/textFieldZip';
import { TextFieldCity } from '../forms/textFieldCity';
import { TextFieldMaxHeartRate } from '../forms/textFieldMaxHeartRate';
import { TextFieldRestingHeartRate } from '../forms/textFieldRestingHeartRate';
import { TextFieldAnaerobicThreshold } from '../forms/textFieldAnaerobicThreshold';
import { TextFieldVentilatoryThreshold } from '../forms/textFieldVentilatoryThreshold';
import { TextFieldTrainingHeartRate } from '../forms/textFieldTrainingHeartRate';
import { TextFieldMinTrainingHeartRate } from '../forms/TextFieldMinTrainingHeartRate';
import { HeartRateCalculator } from './HeartRateCalculator';
import { MainCard } from 'component/cards/MainCard2';
import { CommonSelect } from 'component/select';

const InfoUser = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        user: { role_uuid }
    } = location.state || {};
    const { user } = useFetchUser(role_uuid);
    const formik = useFormikEditUser(user);
    const trainingBackgroundOptions = [
        { value: 10, label: t('users.trainingBackground.occasional') },
        { value: 20, label: t('users.trainingBackground.regular') },
        { value: 30, label: t('users.trainingBackground.frequent') },
        { value: 40, label: t('users.trainingBackground.heavy') },
        { value: 50, label: t('users.trainingBackground.semiPro') },
        { value: 60, label: t('users.trainingBackground.pro') }
    ];
    const typicalDayOptions = [
        { value: 1, label: t('users.typicalDay.mostlyMoving') },
        { value: 2, label: t('users.typicalDay.mostlySitting') },
        { value: 3, label: t('users.typicalDay.mostlyStanding') }
    ];
    const estimatedVo2Max = useMemo(() => {
        const maxHr = Number(formik.values.maxHeartRate);
        const restingHr = Number(formik.values.restingHeartRate);

        if (!maxHr || !restingHr || restingHr <= 0) return null;

        const estimate = 15.3 * (maxHr / restingHr);
        return Number.isFinite(estimate) ? Number(estimate.toFixed(1)) : null;
    }, [formik.values.maxHeartRate, formik.values.restingHeartRate]);
    const isEnglish = i18next.resolvedLanguage?.startsWith('en');
    const estimateLabel = isEnglish ? 'Estimated' : 'Estimativa';
    const useEstimateLabel = isEnglish ? 'Use estimate' : 'Usar estimativa';

    return (
        <form onSubmit={formik.handleSubmit}>
            <MainCard title={t('users.data')}>
                <Grid container spacing={2}>
                    <Grid size={8}>
                        <Grid container spacing={2} sx={{ width: '100%' }}>
                            <Grid
                                size={12}
                                sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                                <AvatarPicker {...formik} />
                            </Grid>
                            <Grid container alignItems="center" size={12}>
                                <Grid size={6}>
                                    <Typography variant="h5">
                                        {t('users.identification')}
                                    </Typography>
                                </Grid>
                                <Grid size={6} sx={{ textAlign: 'right' }}>
                                    <Typography variant="h5" component="span">
                                        Email:
                                    </Typography>{' '}
                                    <Typography variant="body1" component="span">
                                        {user?.email || '-'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid size={12}>
                                <TextFieldName {...formik} />
                            </Grid>
                            <Grid size={6}>
                                <SexSelector {...formik} />
                            </Grid>
                            <Grid size={6}>
                                <GenderSelector {...formik} />
                            </Grid>
                            <Grid size={8}>
                                <TextFieldPhone {...formik} />
                            </Grid>
                            <Grid size={4}>
                                <BirthdatePicker {...formik} />
                            </Grid>
                            <Grid size={12}>
                                <Typography variant="h5">
                                    {t('users.location')}
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <TextFieldAddress {...formik} />
                            </Grid>
                            <Grid size={4}>
                                <TextFieldZip {...formik} />
                            </Grid>
                            <Grid size={5}>
                                <TextFieldCity {...formik} />
                            </Grid>
                            {user?.role === 'PATIENT' && (
                                <>
                                    <Grid size={12} sx={{ mt: 2 }}>
                                        <Typography variant="h5">
                                            {t('users.sections.wearableConfig')}
                                        </Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            fullWidth
                                            label={t('users.fields.height')}
                                            type="number"
                                            name="height"
                                            value={formik.values.height}
                                            onChange={formik.handleChange}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            fullWidth
                                            label={t('users.fields.weight')}
                                            type="number"
                                            name="weight"
                                            value={formik.values.weight}
                                            onChange={formik.handleChange}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            fullWidth
                                            label={t('users.fields.vo2Max')}
                                            type="number"
                                            name="vo2Max"
                                            value={formik.values.vo2Max}
                                            onChange={formik.handleChange}
                                            placeholder={
                                                estimatedVo2Max
                                                    ? String(estimatedVo2Max)
                                                    : undefined
                                            }
                                            helperText={
                                                estimatedVo2Max
                                                    ? `${estimateLabel}: ${estimatedVo2Max}`
                                                    : ''
                                            }
                                        />
                                        {estimatedVo2Max && (
                                            <Button
                                                size="small"
                                                sx={{ mt: 0.5 }}
                                                onClick={() =>
                                                    formik.setFieldValue(
                                                        'vo2Max',
                                                        estimatedVo2Max
                                                    )
                                                }
                                            >
                                                {useEstimateLabel}
                                            </Button>
                                        )}
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            fullWidth
                                            label={t('users.fields.sleepGoalMinutes')}
                                            type="number"
                                            name="sleepGoalMinutes"
                                            value={formik.values.sleepGoalMinutes}
                                            onChange={formik.handleChange}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="training-background-label">
                                                {t('users.fields.trainingBackground')}
                                            </InputLabel>
                                            <CommonSelect
                                                labelId="training-background-label"
                                                name="trainingBackgorund"
                                                label={t(
                                                    'users.fields.trainingBackground'
                                                )}
                                                value={formik.values.trainingBackgorund}
                                                onChange={formik.handleChange}
                                            >
                                                {trainingBackgroundOptions.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </CommonSelect>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="typical-day-label">
                                                {t('users.fields.typicalDay')}
                                            </InputLabel>
                                            <CommonSelect
                                                labelId="typical-day-label"
                                                name="typicalDay"
                                                label={t('users.fields.typicalDay')}
                                                value={formik.values.typicalDay}
                                                onChange={formik.handleChange}
                                            >
                                                {typicalDayOptions.map((option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </CommonSelect>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                    {user?.role === 'PATIENT' && (
                        <>
                            <Divider orientation="vertical" flexItem />
                            <Grid size="grow">
                                <Grid container spacing={2} sx={{ width: '100%' }}>
                                    <Grid size={12}>
                                        <Typography variant="h5">
                                            {t('users.hrAndPerformance')}
                                        </Typography>
                                    </Grid>

                                    {/* Coluna esquerda → inputs */}
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <TextFieldRestingHeartRate {...formik} />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFieldMaxHeartRate {...formik} />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFieldAnaerobicThreshold {...formik} />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFieldVentilatoryThreshold {...formik} />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFieldTrainingHeartRate {...formik} />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFieldMinTrainingHeartRate {...formik} />
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <HeartRateCalculator
                                            birthDate={formik.values.birthdate}
                                            restingHeartRate={
                                                formik.values.restingHeartRate
                                            }
                                            maxHeartRate={formik.values.maxHeartRate}
                                            setFieldValue={formik.setFieldValue}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    )}
                    <Grid
                        size={12}
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}
                    >
                        <SubmitButton
                            type="button"
                            sx={{ mr: 2 }}
                            onClick={() => navigate('/users', { replace: true })}
                        >
                            {t('common.cancel')}
                        </SubmitButton>
                        <SubmitButton
                            disabled={formik.isSubmitting}
                            name="submit"
                            type="submit"
                        >
                            {t('common.save')}
                        </SubmitButton>
                    </Grid>
                </Grid>
            </MainCard>
        </form>
    );
};

export default InfoUser;

